import { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const SUPPORT_APP_NOTIFICATION_EVENT = 'brand2chat:support-app-notification';
const DEFAULT_DURATION_MS = 5000;
const MAX_VISIBLE_NOTIFICATIONS = 3;

const typeStyles = {
  ticket: {
    label: 'Ticket',
    palette: 'primary',
    icon: ConfirmationNumberOutlinedIcon,
  },
  visitor: {
    label: 'Visitor',
    palette: 'secondary',
    icon: PersonAddAltOutlinedIcon,
  },
  message: {
    label: 'Chat',
    palette: 'primary',
    icon: ForumOutlinedIcon,
  },
};

export function showSupportAppNotification(detail) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(SUPPORT_APP_NOTIFICATION_EVENT, {
      detail,
    })
  );
}

export default function SupportAppNotification() {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef({});

  const removeNotification = useCallback((id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }

    setNotifications((current) => current.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    const handleNotification = (event) => {
      const detail = event.detail || {};
      const title = String(detail.title || '').trim();
      const message = String(detail.message || detail.body || '').trim();

      if (!title && !message) {
        return;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const duration = Number(detail.duration || DEFAULT_DURATION_MS);
      const notification = {
        id,
        title: title || 'Notification',
        message,
        type: detail.type || 'message',
        url: detail.url || '',
      };

      setNotifications((current) => [notification, ...current].slice(0, MAX_VISIBLE_NOTIFICATIONS));
      timersRef.current[id] = setTimeout(() => removeNotification(id), duration);
    };

    window.addEventListener(SUPPORT_APP_NOTIFICATION_EVENT, handleNotification);

    return () => {
      window.removeEventListener(SUPPORT_APP_NOTIFICATION_EVENT, handleNotification);
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
  }, [removeNotification]);

  if (!notifications.length) {
    return null;
  }

  const openNotification = (item) => {
    if (!item.url) {
      return;
    }

    window.location.assign(item.url);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 24 },
        bottom: { xs: 14, sm: 24 },
        zIndex: (theme) => theme.zIndex.snackbar + 20,
        width: { xs: 'calc(100vw - 24px)', sm: 390 },
        pointerEvents: 'none',
      }}
    >
      <Stack spacing={1.25}>
        {notifications.map((item) => {
          const style = typeStyles[item.type] || typeStyles.message;
          const NotificationIcon = style.icon;
          const isClickable = Boolean(item.url);
          const getPaletteColor = (theme) => theme.palette[style.palette]?.main || theme.palette.primary.main;

          return (
            <Paper
              key={item.id}
              elevation={8}
              role={isClickable ? 'button' : 'status'}
              tabIndex={isClickable ? 0 : -1}
              onClick={() => openNotification(item)}
              onKeyDown={(event) => {
                if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  openNotification(item);
                }
              }}
              sx={{
                position: 'relative',
                overflow: 'hidden',
                p: 1.6,
                borderRadius: 3,
                color: 'text.primary',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.common.white,
                backgroundImage: (theme) => `linear-gradient(135deg,
                  ${alpha(theme.palette[style.palette]?.light || getPaletteColor(theme), 0.24)} 0%,
                  ${alpha(theme.palette.background.paper, 0.98)} 44%,
                  ${alpha(theme.palette.secondary.light || theme.palette.secondary.main, 0.16)} 100%)`,
                border: (theme) => `1px solid ${alpha(getPaletteColor(theme), 0.35)}`,
                boxShadow: (theme) => `0 22px 56px ${alpha(getPaletteColor(theme), 0.28)}`,
                cursor: isClickable ? 'pointer' : 'default',
                pointerEvents: 'auto',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                  transform: isClickable ? 'translateY(-2px)' : 'none',
                  boxShadow: (theme) => `0 26px 64px ${alpha(getPaletteColor(theme), 0.36)}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 5,
                  bgcolor: (theme) => getPaletteColor(theme),
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  background: (theme) =>
                    `radial-gradient(circle at 14% 12%, ${alpha(getPaletteColor(theme), 0.18)} 0%, transparent 32%)`,
                },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2.25,
                    background: (theme) =>
                      `linear-gradient(145deg, ${getPaletteColor(theme)}, ${theme.palette.secondary.main})`,
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: (theme) => `0 12px 24px ${alpha(getPaletteColor(theme), 0.35)}`,
                  }}
                >
                  <NotificationIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Chip
                      label={style.label}
                      size="small"
                      sx={{
                        height: 20,
                        borderRadius: 99,
                        bgcolor: (theme) => alpha(getPaletteColor(theme), 0.12),
                        color: (theme) => getPaletteColor(theme),
                        fontSize: 11,
                        fontWeight: 800,
                        '.MuiChip-label': { px: 1 },
                      }}
                    />
                    <Typography sx={{ fontSize: 14.5, fontWeight: 900, color: 'text.primary' }} noWrap>
                      {item.title}
                    </Typography>
                  </Stack>
                  {item.message && (
                    <Typography
                      sx={{
                        mt: 0.65,
                        color: 'text.secondary',
                        fontSize: 13,
                        lineHeight: 1.45,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.message}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  size="small"
                  aria-label="Dismiss notification"
                  onClick={(event) => {
                    event.stopPropagation();
                    removeNotification(item.id);
                  }}
                  sx={{
                    mt: -0.75,
                    mr: -0.75,
                    color: 'text.secondary',
                    '&:hover': {
                      bgcolor: (theme) => alpha(getPaletteColor(theme), 0.12),
                      color: (theme) => getPaletteColor(theme),
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
